import { useState, useEffect, useRef, useContext } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import "leaflet-spin";
import TokuteishohyokiMain from "../TokuteishohyokiMain";
import {
  createFeaturePropertyKey,
} from "../../utils/Util";
import { MapContext } from "../../context/MapContext";
import { getOrderHistoryMap } from "../../utils/MapManager";
import { HUDE_COLORS } from "../../constants/Constant";
import FeatureDetail from "../FeatureDetail";
import TohonBuyListTable from "../Tohon/TohonBuyListTable";


// Leafletのデフォルトアイコン設定を修正
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

const mojcopyright_leaflet =
  '<a href="https://front.geospatial.jp/" target="_blank" rel="noopener noreferrer">登記所備付地図<br>データ（法務省）</a>';

const NiniMapModal = ({ show, handleClose, file, ninix, niniy }) => {
  const { isReflectHistoryOnMap } = useContext(MapContext);

  const mapRef = useRef(null);
  const leafletMap = useRef(null);
  const addNiniGeojsonLayer = useRef(null);
  const leaflet_marker = useRef(null);

  const [selectedMap, setSelectedMap] = useState("");
  const [isSidebarOpen, setSidebarOpen] = useState(false);
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [isSelectDisabled, setSelectDisabled] = useState(false);

  useEffect(() => {
    if (show && !leafletMap.current) {
      // マップの初期化
      leafletMap.current = L.map(mapRef.current, {
        crs: L.CRS.Simple,
        minZoom: -3,
        maxZoom: 10,
      });
      leafletMap.current.attributionControl.setPrefix(false);
      leafletMap.current.attributionControl.addAttribution(
        mojcopyright_leaflet
      );

      leafletMap.current.on("zoomend", function () {
        if (leafletMap.current.getZoom() < 4) {
          document.querySelectorAll(".leaflet-tooltip").forEach((el) => {
            el.style.display = "none";
          });
        } else {
          document.querySelectorAll(".leaflet-tooltip").forEach((el) => {
            el.style.display = "block";
          });
        }
      });
    }

    if (show && file && file.length > 0) {
      const initialFileName = file[0].FileNameELEM;
      setSelectedMap(initialFileName);
      DisplayNiniMap(initialFileName, ninix, niniy);
    }

    // クリーンアップ
    return () => {
      if (leafletMap.current) {
        // メモリリークを防ぐためにマップを削除
        leafletMap.current.remove();
        leafletMap.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  // 地物のプロパティに基づいてスタイルを返す関数
  const getFeatureStyle = (feature) => {
    // キー生成
    const featureKey = createFeaturePropertyKey(feature.properties);
    const isPurchased = getOrderHistoryMap().has(featureKey);

    return {
      color: HUDE_COLORS.nini_line,
      weight: 1,
      opacity: 0.5,
      fillColor:
        isReflectHistoryOnMap && isPurchased
          ? HUDE_COLORS.purchased_fill // 購入済みなら公共と同じ青
          : HUDE_COLORS.nini_fill, // 未購入なら赤系
      fillOpacity: 0.2,
    };
  };

  const setLeafletSpin = () => {
    setSelectDisabled(true);
    if (leafletMap.current) {
      leafletMap.current.spin(true);
    }
  };

  const DisplayNiniMap = (XMLFileName, x, y) => {
    setLeafletSpin();
    const NiniGeojonURL =
      process.env.REACT_APP_JSON_NINI_URL +
      XMLFileName.replace(".xml", "_hude.geojson");

    fetch(NiniGeojonURL)
      .then((response) => response.json())
      .then((data) => {
        if (addNiniGeojsonLayer.current) {
          leafletMap.current.removeLayer(addNiniGeojsonLayer.current);
        }
        if (leaflet_marker.current) {
          leafletMap.current.removeLayer(leaflet_marker.current);
        }
        setSelectedFeature(null);

        addNiniGeojsonLayer.current = new L.geoJSON(data, {
          style: getFeatureStyle, // 関数を適用
          onEachFeature: function onEachFeature(feature, layer) {
            let chibanLabel_nini = "";
            if (feature.properties.大字名)
              chibanLabel_nini += feature.properties.大字名;
            if (feature.properties.丁目名)
              chibanLabel_nini += feature.properties.丁目名;
            if (feature.properties.小字名)
              chibanLabel_nini += feature.properties.小字名;
            if (feature.properties.予備名)
              chibanLabel_nini += feature.properties.予備名;
            if (feature.properties.地番)
              chibanLabel_nini += feature.properties.地番;

            layer.bindTooltip(chibanLabel_nini, {
              permanent: true,
              direction: "center",
              className: "lotno",
            });

            layer.on("mouseover", function () {
              this.setStyle({ fillOpacity: 0.5 });
            });
            layer.on("mouseout", function () {
              this.setStyle({ fillOpacity: 0.2 });
            });
            layer.on("click", function (e) {
              if (leaflet_marker.current) {
                leafletMap.current.removeLayer(leaflet_marker.current);
              }
              leaflet_marker.current = L.marker(e.latlng).addTo(
                leafletMap.current
              );

              setSidebarOpen(true);
              setSelectedFeature(feature);
            });
          },
        }).addTo(leafletMap.current);

        leafletMap.current.fitBounds(addNiniGeojsonLayer.current.getBounds());
        leafletMap.current.invalidateSize();

        if (!isNaN(x) && !isNaN(y) && x !== null && y !== null) {
          leafletMap.current.setView([y, x], 4);
        }
      })
      .catch((error) => console.log(error))
      .finally(() => {
        setSelectDisabled(false);
        if (leafletMap.current) {
          leafletMap.current.spin(false);
        }
      });
  };

  const handleMapSelectionChange = (e) => {
    const newFileName = e.target.value;
    setSelectedMap(newFileName);
    DisplayNiniMap(newFileName, null, null);
  };

  const onModalHide = () => {
    if (addNiniGeojsonLayer.current) {
      leafletMap.current?.removeLayer(addNiniGeojsonLayer.current);
      addNiniGeojsonLayer.current = null;
    }
    if (leaflet_marker.current) {
      leafletMap.current?.removeLayer(leaflet_marker.current);
      leaflet_marker.current = null;
    }
    leafletMap.current?.spin(false);
    setSelectedFeature(null);
    setSidebarOpen(false);
    handleClose();
  };

  return (
    <Modal show={show} onHide={onModalHide} dialogClassName="leaflet-modal">
      <Modal.Header closeButton>
        <Form.Select
          id="select_ninimap"
          size="lg"
          title="複数の地図から選択します"
          onChange={handleMapSelectionChange}
          value={selectedMap}
          disabled={isSelectDisabled}
        >
          {file?.map((f, i) => (
            <option key={i} value={f.FileNameELEM}>
              {`${f.FileNameELEM}　${f.L_File_SIF_OAZA.join(" ")}`}
            </option>
          ))}
        </Form.Select>
      </Modal.Header>
      <Modal.Body id="leaflet-modal-body">
        {/* 背景色は Leaflet 既定（.leaflet-container の #ddd）を inline style で上書き */}
        <div id="leafletMap" ref={mapRef} style={{ background: "#f5f5f5" }}></div>
        <div className={`leaflet-side ${isSidebarOpen ? "active" : ""}`}>
          <div
            className={`leaflet-side-open ${isSidebarOpen ? "active" : ""}`}
            id="leaflet--gsi--side--open"
            onClick={() => setSidebarOpen(!isSidebarOpen)}
          ></div>
          <div
            className={`leaflet-side__inner ${isSidebarOpen ? "active" : ""}`}
          >
            <ul
              className="datalist p-0"
              id="leaflet--gsi--vector--datalist"
            >
              {selectedFeature && (
                <FeatureDetail feature={selectedFeature} isMainMap={false} />
              )}
            </ul>
            <TohonBuyListTable isMainMap={false} />

            <div className="accordion w-100" id="accordion_LeftSlider">
              <button
                type="button"
                className="p-2 btn btn-link btn-sm"
                data-toggle="collapse"
                data-target="#collapseSpecifiedCommercialTransactions_leftSlider"
                aria-expanded="false"
                aria-controls="collapseSpecifiedCommercialTransactions_leftSlider"
              >
                特定商取引法に基づく表記
              </button>
              <div
                className="collapse w-100"
                id="collapseSpecifiedCommercialTransactions_leftSlider"
                data-parent="#accordion_LeftSlider"
              >
                <div className="card card-body w-100 border-1">
                  <TokuteishohyokiMain />
                </div>
              </div>
            </div>

            <div
              className="justify-content-start"
              style={{ marginRight: "10px" }}
            >
              <a
                type="button"
                className="btn btn-link btn-sm"
                role="button"
                href="https://labs.mapple.com/mapple03/mojmap/marual_mapple_registry.pdf"
                target="_blank"
                rel="noopener noreferrer"
              >
                不動産登記情報（土地）の購入方法
              </a>
              <a href="https://youtu.be/0kV-AVe55Mo" data-lity="data-lity">
                <i
                  className="fa fa-youtube-play"
                  style={{
                    color: " #787878",
                    fontSize: "23px",
                    display: "inline-block",
                    verticalAlign: "middle",
                  }}
                  aria-hidden="true"
                ></i>
              </a>
            </div>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="primary" className="btn-block" onClick={onModalHide}>
          地図に戻る
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default NiniMapModal;
